// Sub-account API client (M46) — employee/affiliate handle sharing.
// Mirrors the authed fetch pattern used across the api/ layer (Bearer session id).

import type { Result } from '@shared/types/result.ts';
import { ok, err } from '@shared/types/result.ts';

export type SubAccountStatus = 'pending' | 'active' | 'inactive' | 'rejected';

// Owner-facing employee row (child identity joined in).
export type EmployeeView = Readonly<{
  readonly sub_account_id: number;
  readonly child_user_id: number;
  readonly child_wallet_address: string;
  readonly child_handle: string;
  readonly child_provider: 'fio' | 'ens';
  readonly status: SubAccountStatus;
  readonly seen: boolean;
  readonly created_at: number;
}>;

// Employee-facing business row (parent identity joined in).
export type BusinessView = Readonly<{
  readonly sub_account_id: number;
  readonly parent_user_id: number;
  readonly parent_wallet_address: string;
  readonly parent_handle: string;
  readonly parent_provider: 'fio' | 'ens';
  readonly status: SubAccountStatus;
  readonly seen: boolean;
  readonly allows_tips: boolean;
  readonly created_at: number;
}>;

export type SubAccountAlerts = Readonly<{
  readonly ownerAlerts: ReadonlyArray<EmployeeView>;
  readonly employeeAlerts: ReadonlyArray<BusinessView>;
}>;

// Shared authed request helper — returns the `data` field on success or a Result error.
const _request = async <T>(
  path: string,
  sessionId: string,
  init?: RequestInit,
): Promise<Result<T>> => {
  try {
    const response = await fetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
        ...(init?.headers ?? {}),
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: response.statusText }));
      return err(typeof body.error === 'string' ? body.error : `Request failed (${response.status})`);
    }
    const json = await response.json().catch(() => ({ data: undefined }));
    return ok(json.data as T);
  } catch (e) {
    return err(`Network error: ${e instanceof Error ? e.message : String(e)}`);
  }
};

export const joinBusiness = (
  sessionId: string,
  businessHandle: string,
): Promise<Result<unknown>> =>
  _request('/api/sub-accounts/join', sessionId, {
    method: 'POST',
    body: JSON.stringify({ businessHandle }),
  });

export const respondToRequest = (
  sessionId: string,
  subAccountId: number,
  approve: boolean,
): Promise<Result<unknown>> =>
  _request(`/api/sub-accounts/${subAccountId}/respond`, sessionId, {
    method: 'POST',
    body: JSON.stringify({ approve }),
  });

export const deactivateSubAccount = (
  sessionId: string,
  subAccountId: number,
): Promise<Result<unknown>> =>
  _request(`/api/sub-accounts/${subAccountId}/deactivate`, sessionId, { method: 'POST' });

export const setSubAccountsEnabled = (
  sessionId: string,
  enabled: boolean,
): Promise<Result<unknown>> =>
  _request('/api/sub-accounts/enabled', sessionId, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });

export const getSubAccountsStatus = (
  sessionId: string,
): Promise<Result<{ readonly enabled: boolean; readonly tipsEnabled: boolean }>> =>
  _request('/api/sub-accounts/status', sessionId);

// Business-general tip opt-in (M57).
export const setBusinessTips = (
  sessionId: string,
  enabled: boolean,
): Promise<Result<unknown>> =>
  _request('/api/sub-accounts/tips', sessionId, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });

// Waiter's per-relationship tip override (M57) — only the sub-account holder may set it.
export const setWaiterTips = (
  sessionId: string,
  subAccountId: number,
  allowsTips: boolean,
): Promise<Result<unknown>> =>
  _request(`/api/sub-accounts/${subAccountId}/employee-tips`, sessionId, {
    method: 'POST',
    body: JSON.stringify({ allowsTips }),
  });

export const listEmployees = (
  sessionId: string,
): Promise<Result<ReadonlyArray<EmployeeView>>> =>
  _request('/api/sub-accounts/employees', sessionId);

export const listBusinesses = (
  sessionId: string,
): Promise<Result<ReadonlyArray<BusinessView>>> =>
  _request('/api/sub-accounts/businesses', sessionId);

export const getAlerts = (
  sessionId: string,
): Promise<Result<SubAccountAlerts>> =>
  _request('/api/sub-accounts/alerts', sessionId);

export const markAlertSeen = (
  sessionId: string,
  subAccountId: number,
): Promise<Result<unknown>> =>
  _request(`/api/sub-accounts/${subAccountId}/seen`, sessionId, { method: 'POST' });
